/**
 * "Since yesterday" — V10 §1.
 *
 * A session that outlived its own day has to say so, and it has to say so in
 * words rather than by rendering an elapsed counter that reads "17h 12m" and
 * looks like a bug. Pure, and separate from the chip, because the arithmetic
 * ("is this work date before today, and by how much?") is the part worth
 * proving and the part that is wrong if anyone reaches for a `Date` and a
 * local timezone to do it.
 */

const DAY_MS = 86_400_000;

/** `YYYY-MM-DD` difference in whole days, with no timezone involved. */
export function daysBefore(date: string, reference: string): number {
  const asUtc = (value: string): number => {
    const [year, month, day] = value.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
  };
  return Math.round((asUtc(reference) - asUtc(date)) / DAY_MS);
}

/**
 * How to name the day an open session began on, relative to today.
 *
 * `null` when it began today — the caller shows its ordinary elapsed label in
 * that case, which is the overwhelmingly common one.
 */
export function openSinceLabel(workDate: string, today: string): string | null {
  const days = daysBefore(workDate, today);
  if (days <= 0) return null;
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

/** Whether an open session belongs to a day that has already ended. */
export function isStaleSession(
  open: { workDate: string } | null | undefined,
  today: string,
): boolean {
  return Boolean(open && daysBefore(open.workDate, today) > 0);
}
